import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Trophy, Info } from "lucide-react";
import api from "@/lib/api";
import { SectionHeader, DLLoading, EmptyState, Slogan } from "@/components/dl/ui";

function Th({ children, className = "" }) {
  return <th className={`px-3 py-2 text-left text-[10px] font-mono uppercase tracking-wider text-white/40 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-3 text-sm ${className}`}>{children}</td>;
}

export default function DLStandings() {
  const { caseId, team } = useOutletContext();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    api.get(`/dl/cases/${caseId}/standings`).then(({ data }) => setData(data)).catch(() => setData({ standings: [] }));
    if (team) api.get(`/dl/teams/${team.dl_team_id}/score-events`).then(({ data }) => setHistory(data)).catch(() => setHistory(null));
  }, [caseId, team]);

  return (
    <div className="space-y-8">
      <SectionHeader kicker="Case Standings" title="Where teams stand." desc="A public competitive scoreboard. No team's private strategy is ever shown here." />

      {data === null ? <DLLoading label="Tallying the board…" /> :
        (data.standings || []).length === 0 ? (
          <EmptyState icon={Trophy} title="No teams on the board yet" desc="Once teams form and the Commissioner awards points, standings appear here." testid="dl-standings-empty" />
        ) : (
          <div className="dl-panel rounded-lg overflow-x-auto">
            <table className="w-full min-w-[720px]" data-testid="dl-standings-table">
              <thead className="border-b border-white/10 bg-white/[0.02]">
                <tr>
                  <Th>Rank</Th><Th>Team</Th><Th className="text-right">Total</Th>
                  <Th className="text-right">Playbook</Th><Th className="text-right">Case</Th>
                  <Th className="text-right">Cap Left</Th><Th className="text-right">Hrs Left</Th>
                  <Th className="text-right">Locked</Th><Th className="text-right">Confirmed</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {data.standings.map((r) => {
                  const mine = team && r.dl_team_id === team.dl_team_id;
                  return (
                    <tr key={r.dl_team_id} className={mine ? "bg-gold/[0.07]" : ""} data-testid="dl-standings-row">
                      <Td className="font-display font-bold text-gold">#{r.rank}</Td>
                      <Td className="font-semibold text-white">{r.team_name}{mine && <span className="ml-2 text-[10px] font-mono text-gold/70">YOU</span>}</Td>
                      <Td className="text-right font-display font-bold text-white">{r.total_points}</Td>
                      <Td className="text-right text-sky-300">{r.playbook_points}</Td>
                      <Td className="text-right text-gold">{r.case_points}</Td>
                      <Td className="text-right text-white/70">{r.cap_remaining ?? "—"}</Td>
                      <Td className="text-right text-white/70">{r.attorney_hours_remaining ?? "—"}</Td>
                      <Td className="text-right text-white/70">{r.locked_play_count}</Td>
                      <Td className="text-right text-emerald-300">{r.confirmed_play_count}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      {data?.ranking_method && (
        <p className="flex items-start gap-2 text-xs text-white/40"><Info className="w-4 h-4 shrink-0 mt-0.5" /> {data.ranking_method}</p>
      )}

      {/* Your team's private scoring history */}
      {team && (
        <div>
          <SectionHeader kicker="Your Scoring History" title={`${team.team_name} — points ledger`} desc="Only your team can see these details." className="mb-4" />
          {(!history || (history.score_events || []).length === 0) ? (
            <EmptyState icon={Trophy} title="No points yet" desc="The Commissioner hasn't awarded your team any points." testid="dl-history-empty" />
          ) : (
            <div className="dl-panel rounded-lg divide-y divide-white/5" data-testid="dl-score-history">
              {history.score_events.slice().reverse().map((e) => (
                <div key={e.score_event_id} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <span className={`text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded-sm ${e.score_type === "CASE" ? "bg-gold/15 text-gold" : "bg-sky-500/15 text-sky-300"}`}>{e.score_type}{e.adjusts_event_id ? " · ADJ" : ""}</span>
                    <span className="text-sm text-white/70 ml-2">{e.reason}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`font-display font-bold ${Number(e.points) < 0 ? "text-red-400" : "text-emerald-300"}`}>{Number(e.points) > 0 ? "+" : ""}{e.points}</span>
                    <span className="text-[10px] text-white/30 font-mono">{(e.awarded_at || "").slice(0, 10)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <Slogan className="block text-center text-white/25 pt-2">No Monday-morning quarterbacking.</Slogan>
    </div>
  );
}
